import { useCallback, useState } from 'react';

import { Session } from '@feathr/blackbox';
import { useAccount } from '@feathr/extender/state';

import type { IDonationCreatedEvent, IRawDonationSSEEvent } from '../../App/FundraisePage/hooks';

interface IUseDebugDonationEndpointReturn {
  /**
   * Calls the backend test endpoint to publish a donation event to the SSE stream.
   * Requires sudoer access. Only works in non-production environments.
   */
  publishTestEvent: (event: IDonationCreatedEvent) => Promise<void>;
  /** True while the request is in flight. */
  isLoading: boolean;
  /** Error message if the request failed. */
  error: string | null;
}

/**
 * Transforms a frontend donation event to the backend's raw SSE format.
 * The backend publishes events in the raw format, so we need to convert before sending.
 * Adds a test marker in cust_params so we can identify debugger events when they return via SSE.
 */
function transformToRawFormat(event: IDonationCreatedEvent): IRawDonationSSEEvent {
  return {
    per_id: event.person_id,
    donor_name: `${event.donor_first_name} ${event.donor_last_name}`.trim(),
    donation_amount: event.amount.toFixed(2),
    donation_type: event.donation_type === 'recurring' ? 'Recurring' : 'One-time',
    form_id: event.form_id,
    form_name: event.form_name,
    growth_credits: event.credits_earned,
    donation_time: event.created_at,
    cust_params: { _test: '1' },
  };
}

/**
 * Hook for calling the backend SSE test publish endpoint.
 * This endpoint publishes a test event directly to the SSE stream channel.
 *
 * Note: Requires sudoer access (403 Forbidden for non-sudoers).
 */
function useDebugDonationEndpoint(): IUseDebugDonationEndpointReturn {
  const account = useAccount();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const publishTestEvent = useCallback(
    async (event: IDonationCreatedEvent): Promise<void> => {
      if (!account?.id) {
        setError('No account available');
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Stream endpoint is at /stream/, not /v1/stream/
        const baseUrl = BLACKBOX_URL.replace(/v1\/$/, '');
        // Transform to raw SSE format before sending to backend
        const rawEvent = transformToRawFormat(event);
        const response = await fetch(`${baseUrl}stream/${account.id}/donations/test`, {
          method: 'POST',
          headers: Session.getHeaders(),
          body: JSON.stringify(rawEvent),
        });

        if (!response.ok) {
          if (response.status === 403) {
            throw new Error('Sudoer access required');
          }
          const errorText = await response.text();
          throw new Error(errorText || `Request failed with status ${response.status}`);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [account?.id],
  );

  return {
    publishTestEvent,
    isLoading,
    error,
  };
}

export default useDebugDonationEndpoint;
